<template>
  <f7-page name="referral" @page:beforein="onPageBeforeIn">
    <f7-navbar :title="$t(screenTitleKey)" back-link></f7-navbar>

    <!--
      Screen-level failure: BOTH halves are down. The eligibility errors
      (not_activated / disabled) come from a trait both
      endpoints share, so they always land together here. This is the ONLY state
      that replaces the whole screen — a code-only failure (an admin rotated the
      gift program) leaves history rendering below (locked decision: the code and
      list halves fail independently).
    -->
    <f7-block v-if="hasScreenError" class="text-align-center referral-screen-error">
      <f7-icon
        f7="exclamationmark_triangle_fill"
        size="44"
        color="orange"
        class="margin-bottom-half"
      ></f7-icon>
      <p class="text-color-gray">{{ screenErrorMessage }}</p>
      <f7-button outline class="margin-top-half referral-retry" @click="refresh">
        {{ $t('wallet.retry') }}
      </f7-button>
    </f7-block>

    <template v-else>
      <!--
        How it works — names BOTH reward amounts AND the activation threshold
        (locked decision 15: omitting the threshold is the overpromise the brief
        warns against). Shown only once the code half has loaded, since the reward
        rates arrive with that payload; before then they are all zero.
      -->
      <f7-block v-if="codeLoaded" class="referral-hero">
        <p class="referral-hero__text">{{ howItWorks }}</p>
      </f7-block>

      <!-- Code + share — only when the link is usable (200 + isActive). -->
      <f7-block v-if="codeHalfState === 'active'" class="referral-code">
        <div class="referral-code__label">{{ $t('referral.code.label') }}</div>
        <button
          class="referral-code__chip"
          :aria-label="$t('referral.code.label')"
          @click="copyCode"
        >
          <span class="referral-code__value">{{ code }}</span>
          <f7-icon f7="doc_on_doc" size="16" class="referral-code__copy-icon"></f7-icon>
        </button>

        <!--
          Gated on shareLink presence: the backend always sends a shareLink with
          an active code, but if one were ever missing we degrade to the code chip
          above (still copyable) rather than render a button that no-ops silently.
        -->
        <div v-if="shareLink" class="referral-code__actions">
          <!--
            Copy-link is the PRIMARY, always-present action (locked decision 9):
            reliable in every host, including the Telegram Android webview where
            navigator.share is absent. Copies shareLink VERBATIM.
          -->
          <f7-button fill large class="referral-code__share" @click="copyLink">
            <f7-icon f7="link" size="18" class="referral-code__btn-icon"></f7-icon>
            {{ $t('referral.share.copyLink') }}
          </f7-button>
          <!--
            navigator.share is additive, shown only when the host supports it. No
            TMA share wrapper: shareLink is unwrapped and openTelegramChat would
            CLOSE the Mini App (locked decision 9).
          -->
          <f7-button
            v-if="canNativeShare"
            outline
            large
            class="referral-code__native"
            @click="shareViaSystem"
          >
            <f7-icon f7="square_arrow_up" size="18" class="referral-code__btn-icon"></f7-icon>
            {{ $t('referral.share.cta') }}
          </f7-button>
        </div>
      </f7-block>

      <!--
        Deactivated code (locked decision 14): mintForMember() is a firstOrCreate
        that does not filter on is_active, so a dead link comes back in a
        live-looking 200. Hide the share/copy CTA, show the temporarily-inactive
        copy — but KEEP history rendering below. With one code per member this
        means they have no usable link at all.
      -->
      <f7-block
        v-else-if="codeHalfState === 'inactive'"
        class="text-align-center referral-inactive"
      >
        <f7-icon f7="link" size="28" color="gray" class="margin-bottom-half"></f7-icon>
        <p class="text-color-gray">{{ $t('referral.errors.codeInactive') }}</p>
      </f7-block>

      <!--
        Code half errored on its own — the highest-value case on this screen: an
        admin rotated the gift program, so /referral-code 404s
        REFERRAL_PROGRAM_UNAVAILABLE while /referrals keeps serving history, which
        still renders below.
      -->
      <f7-block v-else-if="codeHalfState === 'error'" class="text-align-center referral-code-error">
        <f7-icon
          f7="exclamationmark_triangle"
          size="28"
          color="orange"
          class="margin-bottom-half"
        ></f7-icon>
        <p class="text-color-gray">{{ codeHalfErrorMessage }}</p>
      </f7-block>

      <!--
        Code half still initializing. Covers both 'loading' (a fetch is in flight)
        and 'empty' (the single pre-fetch render tick before page:beforein fires
        refresh()) so no blank gap shows. 'empty' only occurs pre-fetch — the
        code endpoint is mint-or-fetch, so a 200 always carries a code.
      -->
      <f7-block
        v-else-if="codeHalfState === 'loading' || codeHalfState === 'empty'"
        class="text-align-center referral-code-loading"
      >
        <f7-preloader size="28"></f7-preloader>
      </f7-block>

      <!--
        Money buckets vs promise (locked decision 11). stats.* is authoritative
        for money — never a rewardCents row-sum (locked decision 3). At
        totalReferred === 0 the three "0.00 ج.م" chips read as a broken feature,
        so hide them and let the promise carry the screen. A shop earns nothing,
        so under /suppliers/ neither block renders.
      -->
      <template v-if="codeLoaded && !isSupplierView">
        <div v-if="hasReferralMoney" class="referral-buckets">
          <div v-for="bucket in buckets" :key="bucket.key" class="referral-bucket">
            <div class="referral-bucket__amount">{{ bucket.amount }}</div>
            <div class="referral-bucket__label">{{ bucket.label }}</div>
            <div class="referral-bucket__helper">{{ bucket.helper }}</div>
          </div>
        </div>
        <f7-block v-else class="text-align-center referral-promise">
          <f7-icon f7="gift_fill" size="40" color="purple" class="margin-bottom-half"></f7-icon>
          <h3 class="referral-promise__title">{{ $t('referral.promise.title') }}</h3>
          <p class="referral-promise__body">{{ promiseBody }}</p>
        </f7-block>
      </template>

      <!-- History — fails INDEPENDENTLY of the code half above. -->
      <referral-list
        :rows="rows"
        :loading="loading"
        :error="listErrorMessage"
        @retry="refresh"
      ></referral-list>

      <!--
        Truncation line (locked decision 12): render the 100-row cap plus one
        quiet line built from meta.total — NOT stats.totalReferred, which after a
        program rotation counts fewer rows than the list spans. No client cursor;
        the Honeybadger notify fires from onPageBeforeIn so BE learns it happened.
      -->
      <f7-block v-if="truncatedNotice" class="text-align-center referral-truncated">
        <p class="text-color-gray">{{ truncatedNotice }}</p>
      </f7-block>
    </template>
  </f7-page>
</template>

<script setup>
import { useHoneybadger } from '@honeybadger-io/vue';
import { useClipboard, useShare } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CLUB_TYPES, clubTypeForPath } from '@/clubs/manifest';
import ReferralList from '@/components/referral/ReferralList.vue';
import { useCurrency } from '@/composables/useCurrency';
import { useMemberReferral } from '@/composables/useMemberReferral';
import { useToast } from '@/composables/useToast';
import { REFERRAL_MAX_ROWS } from '@/constants/referral';

// F7 hands the matched route to the page. The /suppliers/ namespace IS the club
// context (useClubContext's rule): the same page, opened at /suppliers/referral/,
// is the shop's view — same code, same list, no money anywhere.
const props = defineProps({
  f7route: {
    type: Object,
    default: null,
  },
});

// useScope: 'global' — the app has a single global message tree (no per-component
// <i18n> blocks); matches ProfilePage and avoids vue-i18n "fall back to root" warns.
const { t } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();
const { showSuccess, showError } = useToast();
const honeybadger = useHoneybadger();

// Copy path — `{ legacy: true }` enables the execCommand fallback so copy still
// works on non-secure origins and in Telegram Android webviews where the async
// Clipboard API is unavailable. MembershipCodeBadge omits this AND swallows the
// error, so a failed copy there is a silent no-op; both are fixed here (locked 9).
const { copy } = useClipboard({ legacy: true });
// navigator.share gate — additive to copy-link, never a replacement.
const { share: nativeShare, isSupported: canNativeShare } = useShare();

const {
  code,
  shareLink,
  isActive,
  rewards,
  stats,
  rows,
  meta,
  loading,
  codeError,
  codeErrorCode,
  codeErrorReason,
  listError,
  listErrorCode,
  listErrorReason,
  error,
  errorCode,
  errorReason,
  refresh,
  toI18nKey,
} = useMemberReferral();

// ── The shop's view ─────────────────────────────────────────────────────────
// A shop earns nothing from its link (owner, 2 Nov 2026): no how-much-you-earn
// line, no buckets, no promise. It still sees its code, the welcome gift its
// sellers get, and who joined (the list hides the reward on its own: every
// shop row carries rewardState 'none').
const isSupplierView = computed(
  () => clubTypeForPath(props.f7route?.url) === CLUB_TYPES.SUPPLIERS,
);
const screenTitleKey = computed(() =>
  isSupplierView.value ? 'supplier.referral.screen.title' : 'referral.screen.title',
);

// ── Screen-level failure (both endpoints down) ──────────────────────────────
// `error` is non-null ONLY when both halves failed; the composable already ran
// the backend's precedence order to pick which code to surface.
const hasScreenError = computed(() => error.value != null);
const screenErrorMessage = computed(() =>
  hasScreenError.value ? t(toI18nKey(errorCode.value, errorReason.value)) : null,
);

// ── Code half state machine ─────────────────────────────────────────────────
//   loading  → first fetch in flight, nothing yet
//   error    → the code endpoint failed on its own (e.g. program rotated → 404)
//   inactive → 200 but the code is deactivated: a live-looking dead link
//   active   → 200 + a usable link
//   empty    → the brief pre-fetch moment before page:beforein fires
const codeHalfState = computed(() => {
  if (codeError.value) return 'error';
  if (!code.value) return loading.value ? 'loading' : 'empty';
  if (!isActive.value) return 'inactive';
  return 'active';
});
const codeLoaded = computed(
  () => codeHalfState.value === 'active' || codeHalfState.value === 'inactive',
);
const codeHalfErrorMessage = computed(() =>
  codeError.value ? t(toI18nKey(codeErrorCode.value, codeErrorReason.value)) : null,
);

// ── How-it-works line — names both reward amounts AND the threshold (locked 15).
// The shop's message reads only {friendAmount}; vue-i18n ignores the rest.
const howItWorks = computed(() =>
  t(isSupplierView.value ? 'supplier.referral.screen.howItWorks' : 'referral.screen.howItWorks', {
    friendAmount: formatCurrency(rewards.value.joinedMemberCents),
    ownerAmount: formatCurrency(rewards.value.ownerCents),
    thresholdAmount: formatCurrency(rewards.value.activationThresholdCents),
  }),
);

// ── Money buckets vs the promise ────────────────────────────────────────────
const hasReferralMoney = computed(() => (stats.value.totalReferred ?? 0) > 0);
const buckets = computed(() => [
  {
    key: 'potential',
    label: t('referral.buckets.potential.label'),
    helper: t('referral.buckets.potential.helper'),
    amount: formatCurrency(stats.value.potentialOwnerCents),
  },
  {
    key: 'underReview',
    label: t('referral.buckets.underReview.label'),
    helper: t('referral.buckets.underReview.helper'),
    amount: formatCurrency(stats.value.awaitingApprovalCents),
  },
  {
    key: 'earned',
    label: t('referral.buckets.earned.label'),
    helper: t('referral.buckets.earned.helper'),
    amount: formatCurrency(stats.value.earnedOwnerCents),
  },
]);
const promiseBody = computed(() =>
  t('referral.promise.body', {
    amount: formatCurrency(rewards.value.activationThresholdCents),
  }),
);

// ── List half — localized string (or null) for ReferralList's `error` prop.
// When the screen-level error is showing we don't render the list at all, so
// this only fires for a list-only failure (e.g. a 429 while the code half is OK).
const listErrorMessage = computed(() =>
  listError.value ? t(toI18nKey(listErrorCode.value, listErrorReason.value)) : null,
);

// meta.total drives the history count; stats.totalReferred drives money. After a
// program rotation the list spans an older code the stats do not cover, so the
// two legitimately differ — never render them as the same figure.
// Optional chaining is defensive: the composable always seeds meta as an object
// ({ ...EMPTY_META }), but this is the one meta read on the unguarded first-paint
// path (outside v-if="codeLoaded"), so we harden it against any future drift.
const truncatedNotice = computed(() =>
  meta.value?.hasMore ? t('referral.list.truncated', { total: meta.value?.total }) : null,
);

// Report the cap once per mount. We render the 100 rows plus one quiet line and
// build NO client cursor (locked 12); the notify tells BE whether >100 referrals
// ever happens in the wild so they can decide if cursor pagination is worth it.
let truncationReported = false;
function reportTruncationOnce() {
  if (truncationReported || !meta.value.hasMore) return;
  truncationReported = true;
  honeybadger.notify(new Error('Referral list truncated at row cap'), {
    context: { total: meta.value.total, cap: REFERRAL_MAX_ROWS },
  });
}

// ── Copy + share ────────────────────────────────────────────────────────────
async function copyLink() {
  if (!shareLink.value) return;
  try {
    await copy(shareLink.value);
    showSuccess(t('referral.share.linkCopied'));
  } catch {
    showError(t('referral.share.copyFailed'));
  }
}

async function copyCode() {
  if (!code.value) return;
  try {
    await copy(code.value);
    showSuccess(t('referral.code.copySuccess'));
  } catch {
    showError(t('referral.code.copyFailed'));
  }
}

async function shareViaSystem() {
  if (!shareLink.value) return;
  try {
    await nativeShare({ title: t(screenTitleKey.value), url: shareLink.value });
  } catch (err) {
    // User dismissing the native sheet is not an error.
    if (err?.name === 'AbortError') return;
    // Any real failure falls back to the reliable copy path.
    await copyLink();
  }
}

// ── Freshness — refetch on page-enter, NO polling timer (locked 10). F7 fires
// page:beforein on first mount AND every re-entry, so returning to the screen
// refetches. reportTruncationOnce runs after the fetch settles (refresh never
// rejects — it is Promise.allSettled inside).
function onPageBeforeIn() {
  // refresh() is Promise.allSettled inside — it never rejects; reportTruncationOnce
  // runs after it settles. Returned (not floated) to satisfy promise/catch-or-return.
  return refresh().then(reportTruncationOnce);
}
</script>

<style scoped>
.referral-hero__text {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--poolit-text-secondary, #4b5563);
}

/* ── Code chip ── */
.referral-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.referral-code__label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--poolit-text-muted, #6b7280);
}

.referral-code__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--f7-list-bg-color, #fff);
  border: 1px solid var(--poolit-primary-500, #8e5bfe);
  border-radius: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.referral-code__chip:active {
  background: rgba(var(--poolit-primary-rgb, 142, 91, 254), 0.08);
}

/* The code is Latin text sitting inline in the RTL Arabic flow. `isolate`
   (NOT `embed`) keeps it a self-contained LTR run without reordering the line
   around it — see PostHeader.vue's .ps-rate__pct. */
.referral-code__value {
  font-family: 'Courier New', Courier, monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--poolit-text-primary, #1f2937);
  direction: ltr;
  unicode-bidi: isolate;
  text-align: start;
}

.referral-code__copy-icon {
  color: var(--poolit-primary-500, #8e5bfe);
}

.referral-code__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 360px;
  margin-top: 4px;
}

.referral-code__btn-icon {
  margin-inline-end: 6px;
}

/* ── Independent code-half states ── */
.referral-inactive,
.referral-code-error,
.referral-code-loading {
  padding-top: 16px;
  padding-bottom: 8px;
}

/* ── Money buckets ── */
.referral-buckets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 8px 16px;
}

.referral-bucket {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 10px;
  border: 1px solid var(--f7-list-item-border-color, #e5e7eb);
  border-radius: 12px;
  background: var(--f7-list-bg-color, #fff);
  text-align: center;
}

.referral-bucket__amount {
  font-size: 15px;
  font-weight: 700;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  color: var(--poolit-text-primary, #1f2937);
}

.referral-bucket__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--poolit-text-secondary, #4b5563);
}

.referral-bucket__helper {
  font-size: 10.5px;
  line-height: 1.4;
  color: var(--poolit-text-muted, #6b7280);
}

/* ── Promise (zero referrals) ── */
.referral-promise__title {
  margin: 4px 0;
  font-size: 18px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-text-primary, #1f2937);
}

.referral-promise__body {
  margin: 0 auto;
  max-width: 320px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--poolit-text-secondary, #4b5563);
}

.referral-truncated p {
  margin: 0;
  font-size: 12px;
}
</style>
